import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, List, message, Space, Typography, Alert } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import apiService from '../services/api';

const { Text, Title } = Typography;

interface PDFUploadModalProps {
  visible: boolean;
  onClose: () => void;
  lead: any;
  fieldName: string;
  stage: string;
  businessArea: string;
}

interface LeadFile {
  id: number;
  file_name: string;
  file_size: number;
  uploaded_at: string;
  user_name: string;
  download_url: string;
}

const PDFUploadModal: React.FC<PDFUploadModalProps> = ({
  visible,
  onClose,
  lead,
  fieldName,
  stage,
  businessArea
}) => {
  const [files, setFiles] = useState<LeadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && lead?.id) {
      fetchFiles();
    }
  }, [visible, lead?.id]);

  const fetchFiles = async () => {
    if (!lead?.id) return;
    
    setLoading(true);
    try {
      const response = await apiService.getLeadFiles(lead.id, {
        stage,
        field_name: fieldName
      });
      
      if (response.success) {
        setFiles(response.files || []);
      }
    } catch (error) {
      message.error('Erro ao carregar ficheiros');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!lead?.id) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_area', businessArea);
      formData.append('stage', stage);
      formData.append('field_name', fieldName);

      const response = await apiService.uploadLeadFile(lead.id, formData);
      
      if (response.success) {
        message.success('Ficheiro carregado com sucesso');
        fetchFiles(); // Refresh the file list
      } else {
        message.error(response.message || 'Erro ao carregar ficheiro');
      }
    } catch (error) {
      message.error('Erro ao carregar ficheiro');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: number) => {
    if (!lead?.id) return;

    // Show confirmation dialog
    Modal.confirm({
      title: 'Confirmar eliminação',
      content: 'Tem a certeza que pretende eliminar este ficheiro?',
      okText: 'Eliminar',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await apiService.deleteLeadFile(lead.id, fileId);
          
          if (response.success) {
            message.success('Ficheiro eliminado com sucesso');
            fetchFiles(); // Refresh the file list
          } else {
            message.error(response.message || 'Erro ao eliminar ficheiro');
          }
        } catch (error) {
          message.error('Erro ao eliminar ficheiro');
        }
      }
    });
  };

  const handleDownload = async (file: LeadFile) => {
    try {
      const response = await fetch(file.download_url);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        message.error('Erro ao descarregar ficheiro');
      }
    } catch (error) {
      message.error('Erro ao descarregar ficheiro');
    }
  };



  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      // Only allow PDF files
      const isPDF = file.type === 'application/pdf';
      if (!isPDF) {
        message.error('Apenas ficheiros PDF são permitidos!');
        return false;
      }
      
      // Check file size (20MB limit)
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('O ficheiro deve ser menor que 20MB!');
        return false;
      }
      
      handleUpload(file);
      return false; // Prevent default upload
    },
    showUploadList: false,
    accept: '.pdf'
  };

  return (
    <Modal
      title={`Documentos Assinados - ${lead?.name || 'Lead'}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Apenas ficheiros PDF são permitidos"
          description="Tamanho máximo: 20MB"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Upload {...uploadProps}>
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            disabled={uploading}
            type="dashed"
            style={{ width: '100%' }}
          >
            Carregar PDF
          </Button>
        </Upload>
      </div>

      <div>
        <Title level={5}>Ficheiros carregados ({files.length})</Title>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            A carregar...
          </div>
        ) : files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            Nenhum ficheiro carregado
          </div>
        ) : (
          <List
            dataSource={files}
            renderItem={(file) => (
              <List.Item
                                 actions={[
                   <Button 
                     key="download" 
                     type="text" 
                     icon={<DownloadOutlined />}
                     onClick={() => handleDownload(file)}
                     title="Descarregar"
                   />,
                   <Button 
                     key="delete" 
                     type="text" 
                     danger
                     icon={<DeleteOutlined />}
                     onClick={() => handleDelete(file.id)}
                     title="Eliminar"
                   />
                 ]}
              >
                <List.Item.Meta
                  avatar={<FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />}
                  title={file.file_name}
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary">
                        Tamanho: {formatFileSize(file.file_size)}
                      </Text>
                      <Text type="secondary">
                        Carregado por: {file.user_name}
                      </Text>
                      <Text type="secondary">
                        Data: {formatDate(file.uploaded_at)}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default PDFUploadModal; 